import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../../store';
import {
  ShoppingCart, Package, Clock, CheckCircle, Truck, CreditCard,
  TrendingUp, AlertCircle, AlertTriangle, Calendar, X, Wallet, Percent,
  ArrowRight, XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const STATUS_LABEL = {
  pending: 'Beklemede', preparing: 'Hazırlanıyor', shipped: 'Kargoda',
  in_transit: 'Sevkiyatta', completed: 'Tamamlandı', cancelled: 'İptal',
};

const STATUS_COLOR = (s) => {
  if (s === 'cancelled') return 'bg-[#eef0f7] text-[#141a3d]';
  if (s === 'completed') return 'bg-green-50 text-green-700';
  return 'bg-gray-100 text-gray-700';
};

const PAYMENT_LABEL = (ps) => ps === 'paid' ? 'Ödendi' : ps === 'on_account' ? 'Cari Borç' : 'Bekliyor';
const PAYMENT_COLOR = (ps) => {
  if (ps === 'paid') return 'bg-green-50 text-green-700';
  if (ps === 'on_account') return 'bg-gray-100 text-gray-600';
  return 'bg-amber-50 text-amber-700';
};

export default function DealerDashboard() {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [dismissedNotif, setDismissedNotif] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const [dashRes, promisesRes] = await Promise.all([
          axios.get(`${API_URL}/api/dealer/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/dealer/payment-promises`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        ]);
        const dashData = dashRes.data;
        dashData.pending_payment_promises = Array.isArray(promisesRes.data) ? promisesRes.data : [];
        setDashboard(dashData);
      } catch (err) {
        setError('Dashboard verileri yüklenemedi');
      } finally { setLoading(false); }
    };
    fetch();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1a224f]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#eef0f7] border border-[#b3bbdc] rounded-lg p-4 text-[#141a3d] flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />{error}
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const pendingPromises = dashboard?.pending_payment_promises || [];
  const hasOverdue = pendingPromises.some(p => p.due_date && new Date(p.due_date) < new Date());

  const mainStats = [
    { label: 'Cari Borç', value: formatCurrency(stats.cari_bakiye || 0), icon: Wallet, link: '/dealer/account', warn: (stats.cari_bakiye || 0) > 0 },
    { label: 'İskonto Oranı', value: `%${stats.iskonto_orani || user?.iskonto_orani || 0}`, icon: Percent },
    { label: 'Bu Ay Sipariş', value: stats.monthly_orders || 0, icon: ShoppingCart },
    { label: 'Bu Ay Sipariş Tutarı', value: formatCurrency(stats.monthly_spending || 0), icon: TrendingUp },
    { label: 'Bekleyen Sipariş', value: stats.pending_orders || 0, icon: Clock, link: '/dealer/orders?status=pending' },
    { label: 'Bekleyen Ödeme', value: stats.pending_payments || 0, icon: CreditCard, link: '/dealer/payments' },
  ];

  const orderStatusStats = [
    { label: 'Hazırlanıyor', value: stats.preparing_orders || 0, icon: Package, link: '/dealer/orders?status=preparing' },
    { label: 'Sevkiyatta', value: stats.shipped_orders || 0, icon: Truck, link: '/dealer/orders?status=shipped' },
    { label: 'Tamamlanan', value: stats.completed_orders || 0, icon: CheckCircle, link: '/dealer/orders?status=completed' },
  ];

  return (
    <div className="space-y-3 sm:space-y-5" data-testid="dealer-dashboard">
      {/* Payment Promise Notification */}
      {pendingPromises.length > 0 && !dismissedNotif && (
        <div className={`rounded-lg p-4 border ${hasOverdue ? 'bg-[#eef0f7] border-[#b3bbdc]' : 'bg-amber-50 border-amber-200'}`} data-testid="payment-promise-notification">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${hasOverdue ? 'text-[#1a224f]' : 'text-amber-600'}`} />
              <div>
                <h3 className={`font-semibold text-sm ${hasOverdue ? 'text-[#0f142e]' : 'text-amber-800'}`}>
                  {hasOverdue ? 'Vadesi Geçmiş Ödeme Sözünüz Var!' : 'Bekleyen Ödeme Sözünüz Var'}
                </h3>
                <div className="mt-2 space-y-1">
                  {pendingPromises.map((p, i) => {
                    const isOverdue = p.due_date && new Date(p.due_date) < new Date();
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Calendar className={`w-3.5 h-3.5 shrink-0 ${isOverdue ? 'text-[#3a4680]' : 'text-amber-500'}`} />
                        <span className={isOverdue ? 'text-[#141a3d] font-medium' : 'text-amber-800'}>
                          {formatCurrency(p.amount)} - Vade: {p.due_date ? new Date(p.due_date).toLocaleDateString('tr-TR') : '-'}
                          {p.description && <span className="text-gray-500 ml-2">({p.description})</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <Link to="/dealer/payments" className={`inline-flex items-center gap-1 text-xs font-medium mt-2 ${hasOverdue ? 'text-[#141a3d] hover:text-[#0f142e]' : 'text-amber-700 hover:text-amber-800'}`}>
                  <CreditCard className="w-3.5 h-3.5" />Ödeme sayfasına git <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
            <button onClick={() => setDismissedNotif(true)} className="p-1 hover:bg-white/50 rounded">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Welcome */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Hoş Geldiniz, {user?.name || 'Bayi'}</h1>
        <p className="text-sm text-gray-500 mt-1">{user?.company_name || 'B2Bonix Bayi Paneli'}</p>
      </div>

      {/* Main Stats */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        {mainStats.map((stat, i) => {
          const Wrap = stat.link ? Link : 'div';
          return (
            <Wrap
              key={i}
              {...(stat.link ? { to: stat.link } : {})}
              className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 ${stat.link ? 'hover:shadow-md hover:border-gray-200 transition-all cursor-pointer' : ''}`}
              data-testid={`dealer-stat-${i}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{stat.label}</p>
                  <p className={`text-lg sm:text-2xl font-bold mt-0.5 tabular-nums ${stat.warn ? 'text-[#1a224f]' : 'text-gray-900'}`}>{stat.value}</p>
                </div>
                <div className="rounded-lg p-2 sm:p-2.5 bg-gray-100 text-gray-500 shrink-0 ml-2">
                  <stat.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
              </div>
            </Wrap>
          );
        })}
      </div>

      {/* Order Status */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Sipariş Durumları</h2>
          <Link to="/dealer/orders" className="text-xs text-[#1a224f] hover:text-[#141a3d] flex items-center gap-1">Tümü <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="p-3 grid grid-cols-3 gap-2">
          {orderStatusStats.map((os, i) => (
            <Link key={i} to={os.link} className="flex flex-col items-center justify-center px-2 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 hover:border-gray-300 border border-transparent transition-all text-center">
              <div className="p-1.5 rounded-md bg-white text-gray-500 shadow-sm mb-1">
                <os.icon className="w-4 h-4" />
              </div>
              <p className="text-lg font-bold text-gray-900 leading-none">{os.value}</p>
              <p className="text-[10px] text-gray-500 truncate mt-0.5 w-full">{os.label}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        <Link to="/dealer/shop" className="bg-white rounded-xl border border-gray-100 p-4 sm:p-5 hover:shadow-md hover:border-gray-200 transition-all flex items-center gap-3" data-testid="shop-link">
          <div className="bg-[#eef0f7] p-2.5 rounded-lg"><ShoppingCart className="w-5 h-5 text-[#1a224f]" /></div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm">Sipariş Ver</h3>
            <p className="text-xs text-gray-500 truncate">Ürünleri incele ve sipariş oluştur</p>
          </div>
        </Link>
        <Link to="/dealer/orders" className="bg-white rounded-xl border border-gray-100 p-4 sm:p-5 hover:shadow-md hover:border-gray-200 transition-all flex items-center gap-3" data-testid="orders-link">
          <div className="bg-gray-100 p-2.5 rounded-lg"><Package className="w-5 h-5 text-gray-500" /></div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm">Siparişlerim</h3>
            <p className="text-xs text-gray-500 truncate">Sipariş durumlarını takip et</p>
          </div>
        </Link>
      </div>

      {/* Recent Orders - 2026 Table Style */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Son Siparişler</h2>
          <Link to="/dealer/orders" className="text-xs text-[#1a224f] hover:text-[#141a3d] flex items-center gap-1">Tümü <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {dashboard?.recent_orders?.length > 0 ? (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="w-10 px-2 py-2 text-center text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Sipariş No</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Tarih</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Tutar</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-32">Ödeme</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-32">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recent_orders.map((order, i) => (
                    <tr key={order.id} className={`border-b border-gray-100 last:border-0 cursor-pointer ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} onClick={() => setSelectedOrder(order)}>
                      <td className="px-2 py-2 text-center text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">#{order.order_number}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{formatDate(order.created_at)}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(order.total_amount)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${PAYMENT_COLOR(order.payment_status)}`}>
                          {PAYMENT_LABEL(order.payment_status)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLOR(order.status)}`}>
                          {STATUS_LABEL[order.status] || order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {dashboard.recent_orders.map((order) => (
                <div key={order.id} className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setSelectedOrder(order)}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">#{order.order_number}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${STATUS_COLOR(order.status)}`}>{STATUS_LABEL[order.status] || order.status}</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-gray-900">{formatCurrency(order.total_amount)}</p>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${PAYMENT_COLOR(order.payment_status)}`}>
                      {PAYMENT_LABEL(order.payment_status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-gray-400">
            <Package className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">Henüz sipariş bulunmuyor</p>
            <Link to="/dealer/shop" className="text-[#1a224f] hover:text-[#141a3d] text-sm mt-2 inline-flex items-center gap-1 font-medium">
              İlk siparişinizi verin <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm p-2 sm:p-4 pb-20 sm:pb-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white w-full sm:max-w-lg rounded-2xl shadow-xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <div>
                <h2 className="text-base font-bold text-gray-900">#{selectedOrder.order_number}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(selectedOrder.created_at)}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-order-modal"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Durum</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLOR(selectedOrder.status)}`}>
                    {STATUS_LABEL[selectedOrder.status] || selectedOrder.status}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Ödeme</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${PAYMENT_COLOR(selectedOrder.payment_status)}`}>
                    {PAYMENT_LABEL(selectedOrder.payment_status)}
                  </span>
                </div>
              </div>
              {selectedOrder.items?.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-2">Ürünler ({selectedOrder.item_count})</p>
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                    {selectedOrder.items.map((item, idx) => (
                      <div key={idx} className={`flex items-center justify-between px-4 py-2.5 ${idx % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-gray-900 truncate">{item.product_name}</p>
                          <p className="text-[11px] text-gray-400">{item.quantity} x {formatCurrency(item.unit_price)}{item.iskonto_orani > 0 ? ` (%${item.iskonto_orani} isk.)` : ''}</p>
                        </div>
                        <p className="text-[13px] font-semibold tabular-nums shrink-0">{formatCurrency(item.total_price)}</p>
                      </div>
                    ))}
                    <div className="bg-gray-50 px-4 py-3 space-y-1">
                      <div className="flex justify-between text-[13px]"><span className="text-gray-400">Tutar</span><span className="font-medium tabular-nums">{formatCurrency(selectedOrder.total_amount)}</span></div>
                      {selectedOrder.items.some(i => i.kdv_orani > 0) && (
                        <div className="flex justify-between text-[13px]"><span className="text-gray-400">KDV</span><span className="font-medium tabular-nums">{formatCurrency(selectedOrder.items.reduce((s, i) => s + (i.unit_price * i.quantity * (i.kdv_orani || 0) / 100), 0))}</span></div>
                      )}
                      <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-gray-200"><span>Toplam</span><span className="tabular-nums text-[#1a224f]">{formatCurrency(selectedOrder.total_amount + selectedOrder.items.reduce((s, i) => s + (i.unit_price * i.quantity * (i.kdv_orani || 0) / 100), 0))}</span></div>
                    </div>
                  </div>
                </div>
              )}
              {selectedOrder.shipping_address && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Adres</p>
                  <p className="text-sm text-gray-700">{selectedOrder.shipping_address}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end px-5 py-3 border-t border-gray-100 bg-gray-50/50 shrink-0">
              <button onClick={() => setSelectedOrder(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
